// Bottom 26px audit strip. Shows the last 20 audit events for the current user
// from /v1/audit/me, polling every 15 seconds. Events scroll left continuously
// via CSS animation. When the fetch fails (e.g. on an expired session) the
// strip silently shows nothing — it must never block page load.

import { apiFetch } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';

interface AuditItem {
  event_id: string;
  ts: string;
  action: string;
  action_class: string;
  result: string;
  request_id: string | null;
}

// Compact label: "tenant.focus_updated · success" or "watchlist_item.added · error · id abc…"
function fmt(item: AuditItem): string {
  const t = new Date(item.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  let s = `${t} · ${item.action} · ${item.result}`;
  if (item.result === 'error' && item.request_id) s += ` · id ${item.request_id}`;
  return s;
}

export function AuditTicker() {
  const tickerRef = useRef<HTMLDivElement | null>(null);

  const { data } = useQuery({
    queryKey: ['audit', 'me', 'ticker'],
    queryFn: () => apiFetch<{ items: AuditItem[] }>('/v1/audit/me?limit=20'),
    // Poll every 15s — audit strip is low-priority; don't hammer the DB.
    refetchInterval: 15_000,
    // A stale ticker is fine. Don't refetch on window focus (tab-switching would
    // cause a visible strip redraw mid-read).
    refetchOnWindowFocus: false,
    // Swallow errors silently — the ticker is purely informational.
    retry: false,
  });

  const items = data?.items ?? [];

  return (
    <div
      style={{
        height: 26,
        flexShrink: 0,
        borderTop: '1px solid var(--bd)',
        background: 'var(--bg-base)',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '0 12px',
        overflow: 'hidden',
        fontSize: 11,
        fontFamily: 'var(--mono)',
        color: 'var(--fg-3)',
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          color: 'var(--fg-2)',
          letterSpacing: 0.4,
          flexShrink: 0,
          borderRight: '1px solid var(--bd)',
          paddingRight: 10,
        }}
      >
        AUDIT
      </span>

      {items.length === 0 ? (
        <span style={{ color: 'var(--fg-3)' }}>—</span>
      ) : (
        <div
          ref={tickerRef}
          style={{ overflow: 'hidden', flex: 1, position: 'relative', height: '100%' }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 24,
              animation: `audit-scroll ${Math.max(20, items.length * 6)}s linear infinite`,
              willChange: 'transform',
              height: '100%',
            }}
          >
            {/* Duplicate items for seamless loop */}
            {[...items, ...items].map((item, i) => (
              <span
                key={`${item.event_id}-${i}`}
                style={{
                  color: item.result === 'error' ? 'var(--rd, #e55)' : 'var(--fg-3)',
                  flexShrink: 0,
                }}
              >
                {fmt(item)}
              </span>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes audit-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}
